// Location autocomplete input. Hits Photon (komoot) on every keystroke
// past 3 characters and renders a city / state / country suggestion
// dropdown beneath the field. Same Photon endpoint the AuthModal +
// Questionnaire have used since plan 20 — extracted here so every form
// that captures a birth location speaks the same UX.
//
// Controlled API: pass `value` + `onChange(value)`. Pass `allowUnknown`
// to render the "Location Unknown" checkbox; toggling it sets value to
// the literal string `'Unknown'` (the on-wire sentinel the server
// already accepts), and the input goes disabled.

import { useId, useState, type ReactNode } from 'react';
import { cn } from '../utils/cn';
import { INPUT_BASE_CLASS, INPUT_DENSITY, type InputDensity } from './FormInput';

interface PhotonFeature {
  properties: {
    name?: string;
    city?: string;
    state?: string;
    country?: string;
  };
}

const UNKNOWN_SENTINEL = 'Unknown';

export interface LocationAutocompleteProps {
  label?: ReactNode;
  id?: string;
  density?: InputDensity;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  allowUnknown?: boolean;
  containerClassName?: string;
}

export const LocationAutocomplete = ({
  label,
  id,
  density = 'comfortable',
  placeholder = 'City, State, Country',
  value,
  onChange,
  allowUnknown = false,
  containerClassName,
}: LocationAutocompleteProps) => {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const isUnknown = value === UNKNOWN_SENTINEL;

  const [suggestions, setSuggestions] = useState<PhotonFeature[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const search = async (query: string) => {
    onChange(query);
    if (query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const response = await fetch(
        `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=5`
      );
      const data = (await response.json()) as { features?: PhotonFeature[] };
      setSuggestions(data.features ?? []);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Location search failed:', err);
    }
  };

  const handleUnknownToggle = (checked: boolean) => {
    onChange(checked ? UNKNOWN_SENTINEL : '');
    setShowSuggestions(false);
  };

  const inputValue = isUnknown ? '' : value;

  return (
    <div className={cn('space-y-1.5 relative', containerClassName)}>
      {label && (
        <label
          htmlFor={inputId}
          className="text-xs font-bold uppercase text-stone-400 ml-1 tracking-wider block"
        >
          {label}
        </label>
      )}
      <input
        id={inputId}
        type="text"
        placeholder={placeholder}
        disabled={isUnknown}
        className={cn(INPUT_BASE_CLASS, INPUT_DENSITY[density], 'border-stone-100')}
        value={inputValue}
        onChange={(e) => search(e.target.value)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        onFocus={() => !isUnknown && inputValue.length >= 3 && setShowSuggestions(true)}
      />
      {showSuggestions && suggestions.length > 0 && !isUnknown && (
        <div className="absolute z-[110] left-0 right-0 top-full mt-2 bg-white border border-stone-200 rounded-2xl shadow-xl overflow-hidden">
          {suggestions.map((feat, idx) => {
            const { name, city, state, country } = feat.properties;
            const formatted = [name, city, state, country].filter(Boolean).join(', ');
            return (
              <button
                key={idx}
                type="button"
                className="w-full text-left px-5 py-3 text-sm hover:bg-stone-50 transition-colors border-b border-stone-100 last:border-0"
                onClick={() => {
                  onChange(formatted);
                  setShowSuggestions(false);
                }}
              >
                {formatted}
              </button>
            );
          })}
        </div>
      )}
      {allowUnknown && (
        <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-stone-300 text-stone-900"
            checked={isUnknown}
            onChange={(e) => handleUnknownToggle(e.target.checked)}
          />
          <span className="text-xs text-stone-600">Location Unknown</span>
        </label>
      )}
    </div>
  );
};
